import { CartelaBingo } from '../eventoAdicionalData';

/* Modelo em /public/bingo-cartela.png, 1024x1423 — coordenadas medidas por
   varredura de pixel sobre a arte final (bordas em rosa/vinho ~ rgb(227,8,72)). */
const W = 1024;
const H = 1423;
const FONT = '"Segoe UI", Arial, Helvetica, sans-serif';
const INK = '#22313f';

const COLUNAS_X = { B: 157, I: 334.5, N: 509.5, G: 684.5, O: 864.5 };
const LINHAS_Y = [497.5, 657.5, 817, 977];

const FOTO_FRAME = { x: 82, y: 1146, w: 254, h: 253, r: 14 };
const CAMPO_NOME = { x: 400, y: 1220, w: 560 };
const CAMPO_RESPONSAVEL = { x: 400, y: 1330, w: 560 };

let templatePromise: Promise<HTMLImageElement | null> | null = null;
const loadTemplate = () => {
  if (!templatePromise) {
    templatePromise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = '/bingo-cartela.png';
    });
  }
  return templatePromise;
};

const photoCache = new Map<string, Promise<HTMLImageElement | null>>();
const loadPhoto = (url: string) => {
  if (!url) return Promise.resolve(null);
  if (!photoCache.has(url)) {
    photoCache.set(
      url,
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      })
    );
  }
  return photoCache.get(url)!;
};

const roundRectPath = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

const drawPhoto = (ctx: CanvasRenderingContext2D, img: HTMLImageElement | null, nome: string) => {
  const frame = FOTO_FRAME;
  ctx.save();
  roundRectPath(ctx, frame.x, frame.y, frame.w, frame.h, frame.r);
  ctx.clip();
  if (img) {
    const scale = Math.max(frame.w / img.width, frame.h / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    ctx.drawImage(img, frame.x + (frame.w - dw) / 2, frame.y + (frame.h - dh) / 2, dw, dh);
  } else {
    ctx.fillStyle = '#e8f3fb';
    ctx.fillRect(frame.x, frame.y, frame.w, frame.h);
    const iniciais = nome
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((parte) => parte[0])
      .join('')
      .toUpperCase();
    ctx.fillStyle = '#1f6f9f';
    ctx.font = `800 60px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(iniciais || '?', frame.x + frame.w / 2, frame.y + frame.h / 2);
  }
  ctx.restore();
};

const fitFont = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, weight: number, maxSize: number, minSize: number) => {
  for (let size = maxSize; size >= minSize; size -= 1) {
    ctx.font = `${weight} ${size}px ${FONT}`;
    if (ctx.measureText(text).width <= maxWidth) return;
  }
};

const drawCampo = (ctx: CanvasRenderingContext2D, texto: string, campo: { x: number; y: number; w: number }) => {
  if (!texto) return;
  ctx.fillStyle = INK;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  fitFont(ctx, texto, campo.w, 700, 26, 14);
  ctx.fillText(texto, campo.x, campo.y);
};

export const gerarCartelaBingoCanvas = async (cartela: CartelaBingo): Promise<HTMLCanvasElement | null> => {
  const [template, foto] = await Promise.all([loadTemplate(), loadPhoto(cartela.fotoUrl || '')]);
  if (!template) return null;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(template, 0, 0, W, H);

  (Object.keys(COLUNAS_X) as Array<keyof typeof COLUNAS_X>).forEach((letra) => {
    const numeros = cartela.numeros[letra] || [];
    const x = COLUNAS_X[letra];
    ctx.fillStyle = '#c81155';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    numeros.slice(0, 4).forEach((numero, indice) => {
      ctx.font = `900 60px ${FONT}`;
      ctx.fillText(String(numero), x, LINHAS_Y[indice]);
    });
  });

  drawPhoto(ctx, foto, cartela.nomeCrianca);
  drawCampo(ctx, cartela.nomeCrianca, CAMPO_NOME);
  drawCampo(ctx, cartela.nomeResponsavel, CAMPO_RESPONSAVEL);

  return canvas;
};

export const gerarCartelaBingoDataUrl = async (cartela: CartelaBingo) => {
  const canvas = await gerarCartelaBingoCanvas(cartela);
  if (!canvas) return null;
  try {
    return canvas.toDataURL('image/png');
  } catch {
    const semFoto = await gerarCartelaBingoCanvas({ ...cartela, fotoUrl: '' });
    return semFoto ? semFoto.toDataURL('image/png') : null;
  }
};

const paraMiniatura = (canvas: HTMLCanvasElement, largura: number) => {
  const mini = document.createElement('canvas');
  mini.width = largura;
  mini.height = Math.round((largura * H) / W);
  const ctx = mini.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(canvas, 0, 0, mini.width, mini.height);
  return mini;
};

export const gerarMiniaturaCartelaBingo = async (cartela: CartelaBingo, largura = 260) => {
  const canvas = await gerarCartelaBingoCanvas(cartela);
  if (!canvas) return null;
  const mini = paraMiniatura(canvas, largura);
  if (!mini) return null;
  try {
    return mini.toDataURL('image/jpeg', 0.85);
  } catch {
    return null;
  }
};

export const baixarDataUrlBingo = (dataUrl: string, filename: string) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
